import { IArtist } from '@/data/models/artist';
import { IOptions } from '@/data/models/option';
import Artist from './Artist';
import './ArtistContainer.scss';
import { useArtistContainer } from '@/hooks/useArtistContainer';
import { IMixedField } from '@/data/models/trackMixed';
import MultiTrackChangeIndicator from '@/components/common/MultiTrackChangeIndicator/MultiTrackChangeIndicator';
import { isEqual } from 'lodash';
import React from 'react';

const defaultArtistRoles = [
  {
    value: 'main',
    label: 'Main',
  },
  {
    value: 'featured',
    label: 'Featured',
  },
  {
    value: 'remixer',
    label: 'Remixer',
  },
] as const satisfies IOptions[];

interface ArtistProp {
  artist: IArtist[] | IMixedField[] | null | undefined;
  originalArtist?: IMixedField[] | null | undefined;
  handleArtist: any;
  errorBanner?: boolean;
  disabled?: boolean;
  artistRoles?: IOptions[];
  multiTrack?: boolean;
  showChangeIndicator?: boolean;
  onFocusName?: () => void;
  onFocusRole?: () => void;
  artistHasErrors?: (index: number) => boolean;
  findArtistErrorMessage?: (index: number) => string;
}

export default function ArtistContainer({
  artist,
  originalArtist,
  handleArtist,
  errorBanner,
  disabled,
  artistRoles,
  multiTrack = false,
  showChangeIndicator = false,
  onFocusName,
  onFocusRole,
  artistHasErrors,
  findArtistErrorMessage,
}: ArtistProp) {
  const roles = artistRoles?.length ? artistRoles : defaultArtistRoles;
  const defaultRole = roles[0]?.label ?? 'Main';

  const {
    inputs,
    handleAddInput,
    handleRoleAndSequenceChange,
    handleChange,
    handleDeleteInput,
    undoChanges,
  } = useArtistContainer({
    initialArtists: artist,
    originalArtist,
    onChange: handleArtist,
    defaultRole,
    multiTrack,
  });

  return (
    <>
      {multiTrack && showChangeIndicator ? (
        <MultiTrackChangeIndicator
          show={!isEqual(artist, originalArtist)}
          onClick={() => undoChanges()}
        ></MultiTrackChangeIndicator>
      ) : null}
      <div className="list-builder">
        {inputs?.map((item: any, index: any) => {
          let artistItem = item;
          let isDisabled = disabled;
          let customInputClass = null;
          let customDropdownClass = null;
          let isMixedNamePlaceholder = false;
          let isMixedRolePlaceholder = false;

          if (multiTrack) {
            if (item?.isReadOnly) {
              isDisabled = item?.isReadOnly;
            }
            artistItem = item.value;
            isMixedNamePlaceholder =
              item.isMixed &&
              artistItem?.name === 'Mixed' &&
              item?.originalValue.name === 'Mixed';
            isMixedRolePlaceholder =
              item.isMixed && artistItem?.performerRole === 'Mixed';
            if (isMixedNamePlaceholder) {
              customInputClass = 'mixed-input-text';
            }
            if (isMixedRolePlaceholder) {
              customDropdownClass = 'mixed-dropdown-text';
            }
          }

          if (!artistItem) return;
          return (
            <Artist
              key={index}
              index={index}
              item={artistItem}
              errorBanner={errorBanner}
              handleChange={handleChange}
              inputs={inputs}
              inputCustomClass={customInputClass}
              dropdownCustomClass={customDropdownClass}
              handleDeleteInput={handleDeleteInput}
              handleAddInput={handleAddInput}
              handleRoleAndSequenceChange={handleRoleAndSequenceChange}
              artistRole={roles}
              disabled={isDisabled}
              mixedPlaceholder={isMixedNamePlaceholder}
              onFocusName={onFocusName}
              onFocusRole={onFocusRole}
              metadataConflictErrorMessage={
                artistHasErrors?.(index)
                  ? findArtistErrorMessage?.(index)
                  : undefined
              }
            ></Artist>
          );
        })}
      </div>
    </>
  );
}
